/**
 * Locale ⇄ user-profile persistence.
 *
 * Kept OUT of the `@/i18n` barrel on purpose: it depends on the API client, and
 * the API client itself reads translated copy from `@/i18n` — importing it from
 * the barrel would create a module cycle.
 *
 *   • `switchLocale(locale)` — what every language switcher calls. Persists the
 *     choice locally (anonymous visitors keep it in localStorage), saves it to
 *     the signed-in user's profile (`PATCH /api/v1/users/me/preferences`), then
 *     reloads so module-level copy is re-evaluated in the new language.
 *   • `syncLocaleWithProfile()` — run once per signed-in session (app shell):
 *     a locale saved on the profile wins over a device-local value (so a choice
 *     follows the user to a new browser); a local explicit choice the profile
 *     does not have yet is pushed up.
 */

import { useEffect } from 'react';

import { getAccessToken } from '@/auth/oidc';
import { getMyPreferences, updateMyPreferences } from '@/lib/api/preferences';

import { isLocale, type Locale } from './config';
import { changeLocale, persistLocale, readLocaleSource, useLocaleStore } from './store';

/** sessionStorage guard so a profile-driven reload can never loop. */
const SYNC_RELOAD_GUARD = 'psn-locale-sync-reloaded';
const PROFILE_SAVE_TIMEOUT_MS = 4000;

/** Page reload hook (overridable in unit tests, where navigation is unavailable). */
export const localeReload = {
  run: (): void => {
    if (typeof window !== 'undefined') window.location.reload();
  },
};

function reloadPage(): void {
  localeReload.run();
}

async function isSignedIn(): Promise<boolean> {
  try {
    return Boolean(await getAccessToken());
  } catch {
    return false;
  }
}

/** Save the locale on the signed-in user's profile; resolves false when it could not. */
export async function saveLocaleToProfile(locale: Locale): Promise<boolean> {
  if (!(await isSignedIn())) return false;
  try {
    await Promise.race([
      updateMyPreferences({ locale }),
      new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), PROFILE_SAVE_TIMEOUT_MS)),
    ]);
    return true;
  } catch {
    // The local choice is already persisted; the next signed-in session retries
    // the push through syncLocaleWithProfile().
    return false;
  }
}

/**
 * Switch the UI language: persist locally, save to the profile when signed in,
 * then reload (default) so every string — including module-level copy — renders
 * in the new language.
 */
export async function switchLocale(locale: Locale, opts: { reload?: boolean } = {}): Promise<void> {
  if (!isLocale(locale)) return;
  persistLocale(locale, 'user');
  // Fetch the target catalog (a lazy chunk unless English) and apply it before
  // the reload — the reloaded page then gets the chunk from the HTTP cache.
  await Promise.all([changeLocale(locale), saveLocaleToProfile(locale)]);
  if (opts.reload !== false) reloadPage();
}

export type ProfileSyncResult = 'applied' | 'pushed' | 'unchanged' | 'unavailable';

/**
 * Reconcile the device locale with the profile. Returns what happened (tests and
 * diagnostics); `applied` triggers one reload into the profile's language.
 */
export async function syncLocaleWithProfile(): Promise<ProfileSyncResult> {
  if (!(await isSignedIn())) return 'unavailable';
  let saved: string | null = null;
  try {
    saved = (await getMyPreferences()).locale ?? null;
  } catch {
    return 'unavailable';
  }
  const current = useLocaleStore.getState().locale;
  if (isLocale(saved)) {
    if (saved === current) {
      persistLocale(current, 'user');
      return 'unchanged';
    }
    persistLocale(saved, 'user');
    await changeLocale(saved);
    try {
      if (!window.sessionStorage.getItem(SYNC_RELOAD_GUARD)) {
        window.sessionStorage.setItem(SYNC_RELOAD_GUARD, '1');
        reloadPage();
      }
    } catch {
      /* storage disabled: the store already switched the hook-driven copy */
    }
    return 'applied';
  }
  if (readLocaleSource() === 'user') {
    return (await saveLocaleToProfile(current)) ? 'pushed' : 'unavailable';
  }
  return 'unchanged';
}

/** Mount once in the authenticated shell. */
export function useProfileLocaleSync(enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    void syncLocaleWithProfile();
  }, [enabled]);
}
